import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Cpu, Loader2, RotateCcw, Zap, Check, X } from "lucide-react";
import Shell from "../components/Shell";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function TestResult({ r }) {
  if (!r) return null;
  return (
    <span data-testid={`test-result-${r.feature}`}
      className={`flex items-center gap-1 text-xs font-mono ${r.ok ? "text-emerald-400" : "text-red-400"}`}
      title={r.ok ? r.reply : r.error}>
      {r.ok ? <Check size={12} /> : <X size={12} />} {r.ok ? `${r.latency_ms} ms` : "failed"}
    </span>
  );
}

function FeatureRow({ f, value, choices, onChange, onTest, testing, result }) {
  return (
    <div data-testid={`ai-feature-row-${f.key}`}
      className="grid grid-cols-1 sm:grid-cols-[1fr_220px_150px] gap-3 items-center py-4 border-b border-border/60">
      <div>
        <div className="text-sm text-foreground">{f.label}</div>
        <div className="text-xs font-mono text-muted-foreground">default · {f.default}</div>
      </div>
      <select data-testid={`ai-model-select-${f.key}`} value={value} onChange={(e) => onChange(f.key, e.target.value)}
        className="bg-transparent border border-border rounded-full px-4 py-2 text-sm font-mono focus:outline-none focus:border-primary">
        {choices.map((c) => <option key={c} value={c} className="bg-[#0B0B12]">{c}</option>)}
      </select>
      <div className="flex items-center gap-3">
        <button data-testid={`ai-test-btn-${f.key}`} onClick={() => onTest(f.key, value)} disabled={testing}
          className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors disabled:opacity-50">
          {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />} Test
        </button>
        <TestResult r={result} />
      </div>
    </div>
  );
}

export default function AdminAI() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});

  const apply = (data) => {
    setCfg(data);
    setDraft(Object.fromEntries(data.features.map((f) => [f.key, f.model])));
  };

  useEffect(() => {
    if (user?.role === "admin") api.get("/admin/ai-models").then(({ data }) => apply(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/home" replace />;

  const dirty = cfg && cfg.features.some((f) => draft[f.key] !== f.model);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/ai-models", { models: draft });
      apply(data);
      toast.success("AI model settings saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const resetAll = () => setDraft(Object.fromEntries(cfg.features.map((f) => [f.key, f.default])));

  const test = async (feature, model) => {
    setTesting(feature);
    try {
      const { data } = await api.post("/admin/ai-models/test", { model });
      setResults((r) => ({ ...r, [feature]: { ...data, feature } }));
      if (!data.ok) toast.error(`${model}: ${data.error || "test failed"}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setTesting(null); }
  };

  return (
    <Shell>
      <div data-testid="admin-ai-page" className="max-w-4xl">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-3">
          <Cpu size={14} /> Admin · AI engine
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mb-3">Model routing</h1>
        <p className="text-muted-foreground mb-10 max-w-2xl">
          Choose which OpenAI model powers each MyNsight feature. Changes apply instantly to all new AI calls (Universal Key).
        </p>
        {!cfg ? (
          <div className="text-muted-foreground font-mono text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> loading…</div>
        ) : (
          <>
            <div className="glass rounded-2xl px-6">
              {cfg.features.map((f) => (
                <FeatureRow key={f.key} f={f} value={draft[f.key]} choices={cfg.choices}
                  onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))}
                  onTest={test} testing={testing === f.key} result={results[f.key]} />
              ))}
            </div>
            <div className="flex items-center gap-4 mt-8">
              <button data-testid="ai-models-save-btn" onClick={save} disabled={!dirty || saving}
                className="py-3 px-8 rounded-full bg-primary text-[#0B0B12] font-medium text-sm disabled:opacity-40 transition-opacity">
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button data-testid="ai-models-reset-btn" onClick={resetAll}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <RotateCcw size={14} /> Reset all to defaults
              </button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
